import { useEffect } from "preact/hooks";
import { countLabel, DataTable, DetailLayout, EmptyState, FigureStrip, fmtNum, PageHeader, RailPanel, SampleMark, StatusBadge } from "../../../../ui/src/index.ts";
import type { TableColumn } from "../../../../ui/src/index.ts";
import { activityByIntent, isLive, outcomeKey } from "../../domain/activity.ts";
import { percent, seconds } from "../../domain/money.ts";
import { DEMONETIZE_MIN_ANSWERS, DEMONETIZE_REFUND_RATE, failedAsks, RATING_PRIOR_ANSWERS, RATING_PRIOR_REFUNDS, ratingStatus } from "../../domain/reputation.ts";
import type { EventRow, HubSummary } from "../../domain/types.ts";
import { loadEvents } from "../actions.ts";
import { HeaderActions } from "../Guide.tsx";
import { Failed, Loading, Section } from "../parts.tsx";
import { href } from "../router.ts";
import { useHubLabel } from "../hubData.ts";
import { useStore } from "../store.ts";
import { BADGE_TONES, capitalize, dayTime, OUTCOME_WORDS } from "../words.ts";

const pct = (share: number) => percent(share, 1);

export function Reputation({ hub }: { hub: HubSummary }) {
  const short = useHubLabel(hub.hub.name);
  const events = useStore((s) => s.events[hub.hub.id]);
  const sample = useStore((s) => s.sample);
  useEffect(() => { void loadEvents(hub.hub.id); }, [hub.hub.id]);

  const label = (id: string | null) => capitalize(hub.manifest.intents.find((i) => i.id === id)?.label ?? id ?? "");
  const header = <PageHeader title={<>Keep your hub's <strong>reputation</strong></>}
    description="How often consumers take back what an answer cost, and how close the hub is to the Exchange's limit."
    action={<HeaderActions />} />;
  const rail = <>
    {hub.stats && <RailPanel title="Time to answer, last 7 days"><p><strong>{seconds(hub.stats.p50Ms)}</strong> median · {seconds(hub.stats.p95Ms)} p95</p></RailPanel>}
  </>;

  const stats = hub.stats;
  if (!stats || stats.answers === 0) return <>{header}<DetailLayout rail={rail}><EmptyState>The hub hasn't answered anything yet, so it has no rating.</EmptyState></DetailLayout></>;

  const s = ratingStatus(stats.refunds, stats.answers);
  const status = !s.rated ? <StatusBadge>Not rated yet</StatusBadge> : s.demonetized ? <StatusBadge tone="danger">Demonetized</StatusBadge> : <StatusBadge tone="success">Within the limit</StatusBadge>;
  const limitContext = !s.rated ? `applies after ${countLabel(s.answersToRated, "more answer")}`
    : s.demonetized ? "over it: the hub gets no asks" : `${countLabel(s.creditsBackLeft, "more credit back", "more credits back")} to reach it`;
  const fill = Math.min(100, (s.rating / s.limit) * 100);

  const failedColumns: TableColumn<EventRow>[] = [
    { id: "when", label: "When", render: (e) => <span class="st-note st-when">{dayTime(e.created_at)}</span> },
    { id: "ask", label: "Ask", fill: true, resizable: true, render: (e) => <span class="st-ask st-cell-text" title={e.question}>{e.question}</span> },
    { id: "question", label: "Question", render: (e) => (e.intent ? <span class="st-q"><a href={href({ name: "question", hub: hub.hub.id, intent: e.intent })} title={label(e.intent)}>{label(e.intent)}</a></span> : "–") },
    { id: "outcome", label: "Outcome", render: (e) => { const k = outcomeKey(e.outcome); return k ? <StatusBadge tone={BADGE_TONES[k]}>{OUTCOME_WORDS[k]}</StatusBadge> : e.outcome; } },
  ];
  const credits = sample.hubs[short]?.creditsBack;
  type CreditRow = { intent: string; answered: number; credits: number };

  let activityBody;
  if (!events || events.status === "loading") activityBody = <Loading label="Loading events" />;
  else if (events.status === "failed") activityBody = <Failed what="the events" error={events.error} retry={() => void loadEvents(hub.hub.id, true)} />;
  else {
    const failed = failedAsks(events.value.events).slice(0, 12);
    activityBody = failed.length ? <DataTable caption={`Errors and empty answers for ${short}`} columns={failedColumns} rows={failed} storageKey="studio-failed-asks" rowKey={(e) => `${e.request_id}-${e.question_id}`} />
      : <EmptyState>No errors or empty answers in the newest events.</EmptyState>;
  }
  let creditsBody;
  if (credits && events?.status === "ok") {
    const activity = activityByIntent(events.value.events);
    const rows: CreditRow[] = hub.manifest.intents.filter(isLive).map((i) => ({ intent: i.id, answered: activity.get(i.id)?.answered ?? 0, credits: credits[i.id] ?? 0 }))
      .filter((r) => r.answered > 0 || r.credits > 0).sort((a, b) => b.credits - a.credits || b.answered - a.answered);
    creditsBody = <DataTable caption={`Credits back by question for ${short}`} storageKey="studio-credits-back" rowKey={(r) => r.intent} rows={rows} columns={[
      { id: "question", label: "Question", fill: true, resizable: true, render: (r) => <span class="st-q"><a href={href({ name: "question", hub: hub.hub.id, intent: r.intent })} title={label(r.intent)}>{label(r.intent)}</a></span> },
      { id: "answered", label: "Answered", numeric: true, render: (r) => fmtNum(r.answered) },
      { id: "credits", label: <>Credits back<SampleMark /></>, numeric: true, render: (r) => fmtNum(r.credits) },
      { id: "rate", label: <>Rate<SampleMark /></>, numeric: true, render: (r) => (r.answered ? percent(r.credits, r.answered) : "–") },
    ]} />;
  }

  return <>
    {header}
    <DetailLayout rail={rail}>
      <div class="st-summary"><FigureStrip label="Credits back and rating" figures={[
        { label: "Credits back", value: percent(stats.refunds, stats.answers), context: `${fmtNum(stats.refunds)} of ${fmtNum(stats.answers)} answers` },
        { label: "Rating", value: pct(s.rating), context: "the Exchange's estimate" },
        { label: "Limit", value: pct(s.limit), context: limitContext },
      ]} /></div>
      <Section title="Against the Exchange's limit" action={status}>
        <div class="st-meter" role="img" aria-label={`Rating ${pct(s.rating)} of a ${pct(s.limit)} limit`}>
          <span class={s.demonetized ? "st-meter__fill st-meter__fill--over" : "st-meter__fill"} style={{ width: `${fill}%` }} />
        </div>
        <p class="st-rail-note">The Exchange stops sending asks to a hub with at least {DEMONETIZE_MIN_ANSWERS} answers whose rating is above {pct(DEMONETIZE_REFUND_RATE)}. Rating = (credits back + {RATING_PRIOR_REFUNDS}) ÷ (answers + {RATING_PRIOR_ANSWERS}), so a new hub isn't judged on a few answers.</p>
      </Section>
      <Section title="Asks that failed" {...(events?.status === "ok" ? { note: "Errors and nothing found · free to the consumer · newest events" } : {})}>
        {activityBody}
      </Section>
      {creditsBody && <Section title="Credits back by question" note="Which answers isn't recorded yet">{creditsBody}</Section>}
    </DetailLayout>
  </>;
}
